import React, { useEffect, useContext, useState, useRef, Suspense, useCallback, useMemo } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import InterfaceContext, { UserInfoContext } from "../../Context";
import { getDistroBrowserIncompatibility } from "../../Util";
import { useParams, useLocation } from "react-router-dom";
import { usePost } from "../../API";
import { Button, useToast } from "@aidkitorg/component-library";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

export function MessageTemplateConfig() {
  const context = useContext(InterfaceContext);
  const user = useContext(UserInfoContext);

  const [messageTemplate, setMessageTemplate] = useState<survey.MessageTemplate>({
    kind: "Message Template",
    version: 1,
    name: "",
    description: "",
    instructions: []
  });
  const [exists, setExists] = useState(false);

  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const createTemplateRequest = usePost('/program/admin/prompts/create');
  const updateTemplateRequest = usePost('/program/admin/prompts/update');
  const findTemplateRequest = usePost('/program/admin/prompts/list');

  const { toast } = useToast();

  const location = useLocation();

  const promptId = useMemo(() => {
    const queryParams = new URLSearchParams(location.search)
    return queryParams.get('promptId');
  }, [location]);

  useAsyncEffect(async () => {
    let templateToUse = messageTemplate;
    if (promptId) {
      const { items: templates } = await findTemplateRequest({ name: promptId });
      const existingTemplate = templates?.[templates?.length - 1];
      if (existingTemplate) {
        setExists(true);
        templateToUse = { ...existingTemplate, kind: 'Message Template' };
        setMessageTemplate(templateToUse);
      }
    }
    // Initialize the distro editor with the message template
    distroRef.current?.initialize(templateToUse, user, undefined, undefined, {},
      `We are using a tool to configure message templates for AI conversations. 
         Message templates define the structure and instructions for AI interactions.`
    );
  }, [user, promptId]);

  const saveTemplate = useCallback(async () => {
    if (exists) {
      const newVersion = (messageTemplate.version || 0) + 1;
      await createTemplateRequest({ item: { ...messageTemplate, version: newVersion } });
      setMessageTemplate(prev => ({ ...prev, version: newVersion }));
      toast({
        description: `created new template version: ${messageTemplate.version} => ${newVersion}`,
        autoClose: true,
        duration: 1_500
      });
    } else {
      await createTemplateRequest({ item: messageTemplate });
      toast({
        description: 'created new template!',
        autoClose: true,
        duration: 1_500
      });
    }
  }, [createTemplateRequest, messageTemplate]);

  const updateTemplate = useCallback(async () => {
    await updateTemplateRequest({ item: messageTemplate });
    toast({
      description: 'updated template!',
      autoClose: true,
      duration: 1_500
    });
  }, [updateTemplateRequest, messageTemplate]);

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  return <div className="bg-gray-100 h-full p-4">
    <h2>Message Template Configuration</h2>
    <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4">
      <Suspense>
        <Distro
          ref={distroRef}
          value={messageTemplate}
          types='src/survey.ts'
          name='MessageTemplate'
          onChange={(v) => {
            setMessageTemplate(v)
          }}
        />
      </Suspense>

      <div className='flex flex-col items-center gap-8'>
        <div className='flex flex-row gap-4'>
          <Button
            className="ml-2 mb-2"
            variant="primary"
            onClick={saveTemplate}>
            {exists ? 'Save New Version' : 'Save'}
          </Button>
          <Button
            className="ml-2 mb-2"
            variant="secondary"
            disabled={!exists}
            onClick={updateTemplate}>Update</Button>
        </div>
      </div>
    </div>
  </div>;
}
